import { useCallback, useContext, useEffect, useState } from 'react'
import { getFlightFareFamilies, searchFlights } from 'api/flights'
import { fareClasses, FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, FlightsFareTypes, FlightViewTypes } from 'constants/flight'
import GeoContext from 'contexts/geoContext'
import { last, nonNullable } from 'lib/array/arrayUtils'
import moment from 'moment'
import { EmptyBaggage, getJourneyFields } from 'lib/checkout/flights/cart'
import useTabVisibility from 'hooks/useTabVisibility'
import { multiCartRefreshFlightItemEvent } from 'analytics/snowplow/events'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'

type FlightDataStatus = 'valid' | 'expired' | 'refreshing' | 'refreshFailed'

function isFlightExpired(expiry: number | undefined) {
  return !!expiry && Date.now() >= expiry
}

function useRefreshMultiCartFlightItem(multiCartItem: App.MultiCartFlightItem): {
  dataStatus: FlightDataStatus
  initiateRefresh: () => void
  refreshStartedAt: number
} {
  const item = multiCartItem.item as App.Checkout.FlightItem
  const { expiry } = item
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)

  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const [dataStatus, setStatus] = useState<FlightDataStatus>(() => isFlightExpired(expiry) ? 'expired' : 'valid')
  const [refreshStartedAt, setRefreshStartedAt] = useState(0)

  const tabIsVisible = useTabVisibility()

  const enableSabreContent = useOptimizelyExperiment(OptimizelyFeatureFlags.flightsEnableSabreContent)

  useEffect(() => {
    if (expiry && dataStatus === 'valid' && tabIsVisible) {
      const millisecondsUntilExpiry = expiry - Date.now()

      if (millisecondsUntilExpiry > 0) {
        const timeout = setTimeout(() => {
          setStatus('expired')
        }, millisecondsUntilExpiry)
        return () => clearTimeout(timeout)
      } else {
        setStatus('expired')
      }
    }
  }, [expiry, dataStatus, tabIsVisible])

  const initiateRefresh = useCallback(async() => {
    const { flights, occupants, forceBundleId } = item

    const isStandalone = !item.bundledItemIds?.length

    if (!flights?.length || !occupants) {
      return
    }

    multiCartRefreshFlightItemEvent(item)
    setStatus('refreshing')
    setRefreshStartedAt(Date.now())

    try {
      let fareType: string
      if (flights.length === 1) {
        fareType = FlightsFareTypes.ONE_WAY
      } else if (flights.length === 2 && flights[0].arrivalAirportCode === flights[1].departingAirportCode && flights[0].departingAirportCode === flights[1].arrivalAirportCode) {
        fareType = FlightsFareTypes.RETURN
      } else {
        fareType = FlightsFareTypes.MULTI_CITY
      }

      let fareClass = flights[0].fareFamily?.cabin.toLowerCase()
      if (!fareClass || !fareClasses.includes(fareClass as typeof fareClasses[number])) {
        fareClass = 'economy'
      }

      const flightSearchResults = await searchFlights({
        currency: currentCurrency,
        region: currentRegionCode,
        occupancies: [occupants],
        itinerary: flights.map(flight => ({
          departureAirport: flight.departingAirportCode,
          arrivalAirport: flight.arrivalAirportCode,
          departureDate: flight.departingDate,
        })) || [],
        fareClass,
        fareType,
        forceBundleId,
        viewType: fareType === FlightsFareTypes.ONE_WAY ? 'OneWay' : undefined, // WHY is this necessary???
      }, { enableSabreContent })

      const { viewType, searchId } = flightSearchResults

      // Only the first segment can be searched by the 'journeyKey' directly, other segments should consider the paired fare ids chain
      // For example SYD > DPS, in Returning segment there can be multiple occurrences of a flight with a specific journey key, but paired to different departing flights
      // If searching solely by the journeyKey without considering the paired fare ids, the algorithm might match an unpaired flight, and the upsell request, booking etc. won't work at all.
      const fares = nonNullable(flights.reduce<Array<App.JourneyV2 | undefined>>((acc, flight, i) => {
        if (i === 0) {
          const firstSegmentFare = flightSearchResults.onewayFares[i].find(fare => fare.bookingInfo.journeyKey === flight.journeyKey)
          acc.push(firstSegmentFare)
        } else {
          const previousSegmentPairedFareIds = acc[i - 1]?.pairedFareId ?? []
          const pairedFare = flightSearchResults.onewayFares[i].find(fare => fare.bookingInfo.journeyKey === flight.journeyKey && previousSegmentPairedFareIds.includes(fare.bookingInfo.boookingKey))
          acc.push(pairedFare)
        }
        return acc
      }, []))

      if (fares.length !== flights.length) {
        // Some or all flights not matched up
        multiCartRefreshFlightItemEvent(item, 'failed')
        setStatus('refreshFailed')
        return
      }

      let upsells: Array<App.FlightFareFamily | undefined> = []

      if (viewType === 'TwoOneWays') {
        let departingUpsell: App.FlightFareFamily | undefined
        let returningUpsell: App.FlightFareFamily | undefined

        if (flights[0].fareFamily) {
          const departingUpsellResults = await getFlightFareFamilies({
            searchId: searchId!,
            sessionId: '',
            origin: flights[0].departingAirportCode,
            destination: flights[0].arrivalAirportCode,
            occupancies: occupants,
            region: currentRegionCode,
            isStandalone,
            selectedFareIds: [fares[0].id],
          })
          departingUpsell = departingUpsellResults.upsellFares.find(upsellFare => upsellFare.fareBasis === flights[0].fareFamily?.fareBasis)
        }

        if (flights[1].fareFamily) {
          const returningUpsellResults = await getFlightFareFamilies({
            searchId: searchId!,
            sessionId: '',
            origin: flights[1].departingAirportCode,
            destination: flights[1].arrivalAirportCode,
            occupancies: occupants,
            region: currentRegionCode,
            departingUpsellOptionId: departingUpsell?.id,
            isStandalone,
            selectedFareIds: [fares[0].id, fares[1].id],
          })
          returningUpsell = returningUpsellResults.upsellFares.find(upsellFare => upsellFare.fareBasis === flights[1].fareFamily?.fareBasis)
        }

        upsells = [departingUpsell, returningUpsell]
      } else if (viewType === 'ReturnTwoOneWays') {
        // For 'ReturnTwoOneWays' view type the last flight is enough to get the origin/destination and properly make a call to the getFlightFareFamilies function (get upsell options)
        // And also the check for fare family presence in the last flight is enough to infer the whole trip has fare families information.
        const lastFlight = last(flights)
        if (lastFlight.fareFamily) {
          const upsellResults = await getFlightFareFamilies({
            searchId: searchId!,
            sessionId: '',
            origin: lastFlight.departingAirportCode,
            destination: lastFlight.arrivalAirportCode,
            occupancies: occupants,
            region: currentRegionCode,
            isStandalone,
            selectedFareIds: fares.map(fare => fare.id),
          })
          upsells = flights.map((originalFare, i) => {
            const upsellFares = upsellResults.upsellFaresPerLeg?.[i] ?? upsellResults.upsellFares
            return upsellFares.find(upsellFare => upsellFare.fareBasis === originalFare.fareFamily?.fareBasis)
          })
        }
      } else if (viewType === 'MultiCity' || viewType === 'OneWay') {
        upsells = await Promise.all(flights.map(async(originalFare, i) => {
          if (originalFare.fareFamily) {
            const upsellResults = await getFlightFareFamilies({
              searchId: searchId!,
              sessionId: '',
              origin: originalFare.departingAirportCode,
              destination: originalFare.arrivalAirportCode,
              occupancies: occupants,
              region: currentRegionCode,
              isStandalone,
              selectedFareIds: fares.map(fare => fare.id),
            })
            const upsellFares = upsellResults.upsellFaresPerLeg?.[i] ?? upsellResults.upsellFares
            return upsellFares.find(upsellFare => upsellFare.fareBasis === originalFare.fareFamily?.fareBasis)
          }
          return undefined
        }))
      } else {
        // Unsupported viewType
        multiCartRefreshFlightItemEvent(item, 'failed')
        setStatus('refreshFailed')
        return
      }

      const expiry = moment(flightSearchResults.timestamp).add(FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, 'minutes').toDate().getTime()

      const updatedItem: App.Checkout.FlightItem = {
        ...item,
        expiry,
        // our flight is changing, reset the fare fetching behaviour
        hasFinalFare: false,
        fareFetchError: undefined,
        journeyType: fares[0].itemType,
        forceBundleId,
        totalFare: 0,
        searchId,
        viewType: fares[0].fareType === FlightViewTypes.RETURN ? FlightViewTypes.TWO_ONE_WAYS_AND_RETURN : item.viewType,
        flights: flights.map((flight, i) => {
          const journey = fares[i]
          const journeyFields = getJourneyFields(journey)

          return {
            ...journeyFields,
            carrier: journey.flightGroup.flights[0].marketingAirline.code,
            extras: flight.extras ?? EmptyBaggage,
            fareFamily: upsells[i],
            departingAirportCode: journey.flightGroup.flights[0].departureAirport,
            arrivalAirportCode: last(journey.flightGroup.flights).arrivalAirport,
            departingDate: journey.flightGroup.flights[0].departingDate,
            arrivalAirportName: last(journey.flightGroup.flights).arrivalAirportName,
            departingAirportName: journey.flightGroup.flights[0].departureAirportName,
            arrivalDateTime: last(journey.flightGroup.flights).arrivalDateTime,
            departureDateTime: journey.flightGroup.flights[0].departureDateTime,
            duration: journey.flightGroup.totalFlightDuration,
            numberOfStops: journey.flightGroup.flights.length - 1,
          }
        }),
      }

      const updatedMultiCartItem: App.MultiCartFlightItem = {
        ...multiCartItem,
        item: updatedItem,
        state: 'active', // Reset save for later by setting state to active
        expiry: updatedItem.expiry,
        flights: updatedItem.flights.map(flight => ({
          journeyId: flight.journeyId,
          journeyKey: flight.journeyKey,
          extras: flight.extras,
          provider: flight.provider || '',
          providerSearchId: flight.providerSearchId || '',
          fareFamily: flight.fareFamily,
          withEmbeddedInsurance: flight.withEmbeddedInsurance,
          cost: flight.cost,
          carrier: flight.carrier,
          carrierImage: flight.carrierImage,
          departingAirportCode: flight.departingAirportCode,
          departingAirportName: flight.departingAirportName,
          arrivalAirportCode: flight.arrivalAirportCode,
          arrivalAirportName: flight.arrivalAirportName,
          departingDate: flight.departingDate,
          arrivalDateTime: flight.arrivalDateTime,
          departureDateTime: flight.departureDateTime,
          duration: flight.duration,
          numberOfStops: flight.numberOfStops,
        })),
        searchId: updatedItem.searchId,
        hasFinalFare: updatedItem.hasFinalFare,
        viewType: updatedItem.viewType,
      }

      multiCartRefreshFlightItemEvent(item, 'success')

      // Update XState with the refreshed multi-cart item
      if (activeCartId) {
        multiCartActorRef.send({
          type: 'update cart item',
          cartId: activeCartId,
          multiCartItem: updatedMultiCartItem,
        })
      }

      setStatus('valid')
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch(e) {
      // log error
      multiCartRefreshFlightItemEvent(item, 'failed')
      setStatus('refreshFailed')
      return
    }
  }, [multiCartActorRef, activeCartId, currentCurrency, currentRegionCode, item, multiCartItem, enableSabreContent])

  return {
    dataStatus,
    initiateRefresh,
    refreshStartedAt,
  }
}

export default useRefreshMultiCartFlightItem
